import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Box, Button, ButtonBase, InputAdornment, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getStateNameByCode } from './dummyData';
import { ONLINE_CART_TOKENS } from './style';
import { scrollToFieldAndFocus } from './validation';

const ERROR_MESSAGES = {
    ssnInvalid: 'Please enter a valid 9-digit Social Security Number.',
    itinInvalid: 'Please enter a valid ITIN. It must start with 9 and use middle digits 50-65, 70-88, 90-92, or 94-99.',
    saveFailed: 'Unable to save the value. Please try again.',
};

function formatTaxId(digits) {
    if (digits.length <= 3) {
        return digits;
    }
    if (digits.length <= 5) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function maskTaxId(formatted) {
    const digits = formatted.replace(/\D/g, '');
    if (digits.length < 9) {
        return formatted;
    }
    return `***-**-${digits.slice(5)}`;
}

function maskTaxIdLast4(last4) {
    return last4 ? `***-**-${last4}` : '';
}

function getPlainTaxIdDigits(value) {
    const rawValue = String(value || '').trim();
    const digits = rawValue.replace(/\D/g, '');

    if (/^[\d\s-]+$/.test(rawValue) && /^\d{9}$/.test(digits)) {
        return digits;
    }

    return '';
}

function getSafeLast4(value) {
    const digits = String(value || '').replace(/\D/g, '');

    return digits.length >= 4 ? digits.slice(-4) : '';
}

function validateTaxId(digits, type) {
    if (digits.length !== 9) {
        return false;
    }
    if (type === 'ssn') {
        if (digits === '000000000') {
            return false;
        }
        if (digits.startsWith('000') || digits.startsWith('666') || digits.startsWith('9')) {
            return false;
        }
        if (digits.slice(3, 5) === '00' || digits.slice(5) === '0000') {
            return false;
        }
        return true;
    }
    if (!digits.startsWith('9')) {
        return false;
    }

    const middleDigits = Number(digits.slice(3, 5));

    return (
        (middleDigits >= 50 && middleDigits <= 65) ||
        (middleDigits >= 70 && middleDigits <= 88) ||
        (middleDigits >= 90 && middleDigits <= 92) ||
        (middleDigits >= 94 && middleDigits <= 99)
    );
}

export default function SSNCollection({
    answers,
    onComplete,
    registerSubmitHandler,
    showSubmitButton = true,
    submitLabel = 'Complete My Order',
    submittingLabel = 'Completing order...',
}) {
    const isRelayBankSelected = false; //answers?.relayBank === 'relay_yes';
    const existingTaxIdType = answers?.ssnType === 'itin' ? 'itin' : 'ssn';
    const existingTaxIdDigits = getPlainTaxIdDigits(answers?.ssnFull);
    const existingTaxIdLast4 = getSafeLast4(answers?.ssnLast4) || getSafeLast4(existingTaxIdDigits);
    const hasProtectedSavedTaxId = Boolean(answers?.ssnProvided && existingTaxIdLast4 && !existingTaxIdDigits);

    const [taxIdType, setTaxIdType] = useState(isRelayBankSelected ? 'ssn' : existingTaxIdType);
    const [ssnRawDigits, setSsnRawDigits] = useState(() => (existingTaxIdType === 'ssn' ? existingTaxIdDigits : ''));
    const [itinRawDigits, setItinRawDigits] = useState(() => (existingTaxIdType === 'itin' ? existingTaxIdDigits : ''));
    const [isEditingSavedTaxId, setIsEditingSavedTaxId] = useState(!hasProtectedSavedTaxId);
    const [showRelayItinField, setShowRelayItinField] = useState(() => existingTaxIdType === 'itin' && Boolean(existingTaxIdDigits));
    const [error, setError] = useState(''); // have either ssn or itin error
    const [itinError, setItinError] = useState('');
    const [touched, setTouched] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const stateFormation = answers?.stateOfFormation || null;
    const stateCode = stateFormation?.stateCode || '';
    const stateLabel = stateFormation?.stateName || getStateNameByCode(stateCode, 'your state');
    const shouldShowSavedTaxId = hasProtectedSavedTaxId && !isEditingSavedTaxId;
    const activeTaxIdLabel = isRelayBankSelected || taxIdType === 'ssn' ? 'Social Security Number' : 'ITIN';

    const ssnIsValid = validateTaxId(ssnRawDigits, 'ssn');
    const itinIsValid = validateTaxId(itinRawDigits, 'itin');
    const activeRawDigits = taxIdType === 'ssn' ? ssnRawDigits : itinRawDigits;
    const activeIsValid = shouldShowSavedTaxId || (taxIdType === 'ssn' ? ssnIsValid : itinIsValid);
    const formatted = formatTaxId(activeRawDigits);
    const displayedValue = shouldShowSavedTaxId ? maskTaxIdLast4(existingTaxIdLast4) : activeRawDigits.length === 9 ? maskTaxId(formatted) : formatted;
    const formattedRelayItin = formatTaxId(itinRawDigits);
    const displayedRelayItin = itinRawDigits.length === 9 ? maskTaxId(formattedRelayItin) : formattedRelayItin;

    const helperItems = useMemo(
        () => [
            {
                icon: <LockOutlinedIcon sx={{ fontSize: 18, color: ONLINE_CART_TOKENS.primary }} />,
                title: 'Encrypted end-to-end',
                text: 'Your SSN is transmitted with 256-bit SSL and stored with AES-256 encryption. Never saved in plain text.',
            },
            {
                icon: <VisibilityOffOutlinedIcon sx={{ fontSize: 18, color: ONLINE_CART_TOKENS.primary }} />,
                title: `Used only for your ${stateLabel} filing`,
                text: `We use your SSN exclusively to file your LLC with ${stateLabel}. It is never shared with third parties or used for credit checks.`,
            },
        ],
        [stateLabel]
    );

    const handleInput = (event) => {
        const digits = String(event.target.value || '').replace(/\D/g, '').slice(0, 9);
        if (taxIdType === 'ssn') {
            setSsnRawDigits(digits);
        } else {
            setItinRawDigits(digits);
        }
        if (touched && digits.length === 9) {
            setError('');
        }
    };

    const handleRelayItinInput = (event) => {
        const digits = String(event.target.value || '').replace(/\D/g, '').slice(0, 9);
        setItinRawDigits(digits);
        if (touched && digits.length === 9) {
            setItinError('');
        }
    };

    const handleBlur = () => {
        setTouched(true);
        if (!activeRawDigits) {
            return;
        }
        if (!activeIsValid) {
            setError(taxIdType === 'ssn' ? ERROR_MESSAGES.ssnInvalid : ERROR_MESSAGES.itinInvalid);
            return;
        }
        setError('');
    };

    const handleRelayItinBlur = () => {
        setTouched(true);
        if (!itinRawDigits) {
            return;
        }
        if (!itinIsValid) {
            setItinError(ERROR_MESSAGES.itinInvalid);
            return;
        }
        setItinError('');
    };

    const handleToggleType = () => {
        setIsEditingSavedTaxId(true);
        setTaxIdType((prev) => (prev === 'ssn' ? 'itin' : 'ssn'));
        setTouched(false);
        setError('');
    };

    const handleChangeSavedTaxId = () => {
        setIsEditingSavedTaxId(true);
        setSsnRawDigits('');
        setItinRawDigits('');
        setTouched(false);
        setError('');
        setItinError('');
    };

    const handleComplete = useCallback(async () => {
        setTouched(true);
        setSubmitError('');
        setError('');
        setItinError('');

        if (shouldShowSavedTaxId) {
            setProcessing(true);
            try {
                await onComplete({
                    ssnProvided: true,
                    ssnFull: answers?.ssnFull || '',
                    ssnLast4: existingTaxIdLast4,
                    ssnType: taxIdType,
                });
                return true;
            } catch (err) {
                setSubmitError(err?.message || ERROR_MESSAGES.saveFailed);
                return false;
            } finally {
                setProcessing(false);
            }
        }

        // debugger
        if (isRelayBankSelected) {
            if (!ssnRawDigits || !ssnIsValid) {
                scrollToFieldAndFocus('ssn-tax-id-input', {
                    selectorTemplate: '#{fieldId}',
                    focusAnchorIfFocusable: true,
                });
                setError(ERROR_MESSAGES.ssnInvalid);
                return false;
            }
            if ((showRelayItinField || itinRawDigits) && itinRawDigits && !itinIsValid) {
                scrollToFieldAndFocus('itin-tax-id-input', {
                    selectorTemplate: '#{fieldId}',
                    focusAnchorIfFocusable: true,
                });
                setItinError(ERROR_MESSAGES.itinInvalid);
                return false;
            }
        } else {
            if (!activeRawDigits || !activeIsValid) {
                scrollToFieldAndFocus('ssn-tax-id-input', {
                    selectorTemplate: '#{fieldId}',
                    focusAnchorIfFocusable: true,
                });
                setError(taxIdType === 'ssn' ? ERROR_MESSAGES.ssnInvalid : ERROR_MESSAGES.itinInvalid);
                return false;
            }
        }

        setProcessing(true);
        try {
            const selectedTaxId = taxIdType === 'itin' ? itinRawDigits : ssnRawDigits;
            await onComplete({
                ssnProvided: Boolean(selectedTaxId),
                ssnFull: selectedTaxId,
                ssnLast4: selectedTaxId ? selectedTaxId.slice(-4) : '',
                ssnType: taxIdType,
            });
            return true;
        } catch (err) {
            setSubmitError(err?.message || ERROR_MESSAGES.saveFailed);
            return false;
        } finally {
            setProcessing(false);
        }
    }, [
        activeIsValid,
        activeRawDigits,
        answers?.ssnFull,
        existingTaxIdLast4,
        isRelayBankSelected,
        itinIsValid,
        itinRawDigits,
        onComplete,
        shouldShowSavedTaxId,
        showRelayItinField,
        ssnIsValid,
        ssnRawDigits,
        taxIdType,
    ]);

    useEffect(() => {
        if (!registerSubmitHandler) {
            return undefined;
        }

        registerSubmitHandler(handleComplete);
        return () => {
            registerSubmitHandler(null);
        };
    }, [handleComplete, registerSubmitHandler]);

    return (
        <Box sx={{ maxWidth: 512, mx: 'auto', width: '100%', fontFamily: ONLINE_CART_TOKENS.fontFamily }}>
            <Box sx={{ border: `1px solid ${ONLINE_CART_TOKENS.border}`, borderRadius: 3, overflow: 'hidden', backgroundColor: ONLINE_CART_TOKENS.surface }}>
                <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
                    <Typography sx={{ fontSize: { xs: '1.5rem' }, lineHeight: 1.2, fontWeight: 700, color: ONLINE_CART_TOKENS.textDisplay }}>
                        One more step before checkout
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: '0.875rem', lineHeight: 1.5, color: ONLINE_CART_TOKENS.textSupport }}>
                        {stateLabel} requires your Social Security Number on all LLC formation filings. This is standard for every LLC formed in the United States — we&apos;ll use it only to submit your paperwork.
                    </Typography>

                    <Box sx={{ mt: 3 }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: ONLINE_CART_TOKENS.textHeading, mb: 1.1 }}>
                            {isRelayBankSelected || taxIdType === 'ssn' ? 'Social Security Number' : 'Individual Taxpayer Identification Number (ITIN)'}
                        </Typography>
                        <TextField
                            id="ssn-tax-id-input"
                            fullWidth
                            type="text"
                            inputProps={{
                                inputMode: 'numeric',
                                autoComplete: 'off',
                                pattern: '[0-9]*',
                                maxLength: 11,
                            }}
                            value={displayedValue}
                            onChange={handleInput}
                            onBlur={handleBlur}
                            placeholder={isRelayBankSelected || taxIdType === 'ssn' ? 'XXX-XX-XXXX' : '9XX-50-XXXX'}
                            error={Boolean(error)}
                            disabled={shouldShowSavedTaxId}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlinedIcon sx={{ color: ONLINE_CART_TOKENS.textMutedSoft }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 50,
                                    borderRadius: 1.5,
                                    fontSize: '1rem',
                                    '& input': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' },
                                },
                            }}
                        />
                        {error ? (
                            <Typography sx={{ mt: 1, fontSize: '0.85rem', color: ONLINE_CART_TOKENS.error, fontWeight: 600 }}>
                                {error}
                            </Typography>
                        ) : null}

                        {shouldShowSavedTaxId ? (
                            <ButtonBase
                                onClick={handleChangeSavedTaxId}
                                sx={{
                                    mt: 0.8,
                                    p: 0,
                                    display: 'inline-flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    textAlign: 'left',
                                    borderRadius: 0,
                                    color: ONLINE_CART_TOKENS.primary,
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    lineHeight: 1.35,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                    '&:focus-visible': {
                                        outline: `3px solid ${ONLINE_CART_TOKENS.ring}`,
                                        outlineOffset: '2px'
                                    }
                                }}
                            >
                                Change {activeTaxIdLabel}
                            </ButtonBase>
                        ) : isRelayBankSelected ? (
                            <>
                                {!showRelayItinField ? (
                                    <ButtonBase
                                        onClick={() => {
                                            setShowRelayItinField(true);
                                            setTaxIdType('ssn');
                                        }}
                                        sx={{
                                            mt: 1,
                                            p: 0,
                                            display: 'inline-flex',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            textAlign: 'left',
                                            borderRadius: 0,
                                            color: ONLINE_CART_TOKENS.primary,
                                            fontSize: '0.78rem',
                                            fontWeight: 700,
                                            lineHeight: 1.35,
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                            '&:focus-visible': {
                                                outline: `3px solid ${ONLINE_CART_TOKENS.ring}`,
                                                outlineOffset: '2px'
                                            }
                                        }}
                                    >
                                        Use ITIN instead of SSN
                                    </ButtonBase>
                                ) : null}
                                {showRelayItinField ? (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: ONLINE_CART_TOKENS.textHeading, mb: 1.1 }}>
                                            Individual Taxpayer Identification Number (ITIN)
                                        </Typography>
                                        <TextField
                                            id="itin-tax-id-input"
                                            fullWidth
                                            type="text"
                                            inputProps={{
                                                inputMode: 'numeric',
                                                autoComplete: 'off',
                                                pattern: '[0-9]*',
                                                maxLength: 11,
                                            }}
                                            value={displayedRelayItin}
                                            onChange={handleRelayItinInput}
                                            onBlur={handleRelayItinBlur}
                                            placeholder="9XX-50-XXXX"
                                            error={Boolean(itinError)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockOutlinedIcon sx={{ color: ONLINE_CART_TOKENS.textMutedSoft }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    height: 50,
                                                    borderRadius: 1.5,
                                                    fontSize: '1rem',
                                                    '& input': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' },
                                                },
                                            }}
                                        />
                                        {itinError ? (
                                            <Typography sx={{ mt: 1, fontSize: '0.85rem', color: ONLINE_CART_TOKENS.error, fontWeight: 600 }}>
                                                {itinError}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                ) : null}
                            </>
                        ) : (
                            <ButtonBase
                                onClick={handleToggleType}
                                sx={{
                                    mt: 0.8,
                                    p: 0,
                                    display: 'inline-flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    textAlign: 'left',
                                    borderRadius: 0,
                                    color: ONLINE_CART_TOKENS.primary,
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    lineHeight: 1.35,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                    '&:focus-visible':{
                                        outline: `3px solid ${ONLINE_CART_TOKENS.ring}`,
                                        outlineOffset: '2px'
                                    }
                                }}
                            >
                                {taxIdType === 'ssn' ? "Use ITIN instead of SSN" : 'Use SSN instead of ITIN'}
                            </ButtonBase>
                        )}
                    </Box>

                    <Box sx={{ mt: 3, border: `1px solid ${ONLINE_CART_TOKENS.borderLight}`, borderRadius: 2, backgroundColor: '#F5FCFB', p: { xs: 2, sm: 2.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <ShieldOutlinedIcon sx={{ color: ONLINE_CART_TOKENS.primary, fontSize: 20 }} />
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: ONLINE_CART_TOKENS.textDisplay }}>
                                How we protect your information
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'grid', rowGap: 2 }}>
                            {helperItems.map((item, index) => (
                                <Box key={index} sx={{ display: 'grid', gridTemplateColumns: '22px 1fr', columnGap: 1.25 }}>
                                    <Box sx={{ mt: 0.2 }}>{item.icon}</Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: ONLINE_CART_TOKENS.textHeading }}>{item.title}</Typography>
                                        <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.5, color: ONLINE_CART_TOKENS.textSupport }}>{item.text}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Temporarily hidden: inline SSN continue CTA replaced by StepWrapper continue button. */}
                    {showSubmitButton ? (
                        <Button
                            variant="contained"
                            onClick={handleComplete}
                            disabled={processing}
                            startIcon={<LockOutlinedIcon />}
                            sx={{
                                mt: 3,
                                height: 52,
                                width: '100%',
                                borderRadius: 1.5,
                                fontSize: '1rem',
                                fontWeight: 700,
                                textTransform: 'none',
                                backgroundColor: '#22c55e',
                                color: ONLINE_CART_TOKENS.surface,
                                boxShadow: 'none',

                                '&:hover': {
                                    backgroundColor: '#22c55e',
                                    boxShadow: 'none',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#22c55e80',
                                    color: ONLINE_CART_TOKENS.surface,
                                },
                            }}
                        >
                            {processing ? submittingLabel : submitLabel}
                        </Button>
                    ) : null}
                    {submitError ? (
                        <Typography sx={{ mt: 1, color: ONLINE_CART_TOKENS.error, fontSize: '0.85rem', fontWeight: 600, textAlign: 'center' }}>
                            {submitError}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
        </Box>
    );
}
